import React, { useMemo } from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'

import { darkBlueFontColor } from 'assets/styles/variables'
import {
  BASELINE_COLOR,
  CHART_COLOR,
} from 'features/trend-detection/components/issue-constants'
import {
  computeShareTimeseries,
  transformChartToTimeseries,
} from 'features/trend-detection/components/mini-timeseries-utils'
import type { Chart } from 'features/trend-detection/types'
import { formatDateLabel } from 'features/trend-detection/utils/helpers'

export type ChartMode = 'count' | 'share'

interface IssueTimeseriesChartProps {
  issueChart: Chart
  projectTotalChart: Chart | null | undefined
  baselineShare: number | null | undefined
  mode: ChartMode
  height?: string | number
}

export const IssueTimeseriesChart: React.FC<IssueTimeseriesChartProps> = ({
  issueChart,
  projectTotalChart,
  baselineShare,
  mode,
  height = '280px',
}) => {
  const data = useMemo(() => {
    if (mode === 'share' && projectTotalChart?.data?.length) {
      return computeShareTimeseries(issueChart, projectTotalChart)
    }
    return transformChartToTimeseries(issueChart)
  }, [issueChart, projectTotalChart, mode])

  const formattedData = useMemo(
    () => data.map((d) => ({ ...d, date: formatDateLabel(d.date) })),
    [data],
  )

  const showBaseline =
    mode === 'share' &&
    baselineShare != null &&
    isFinite(baselineShare) &&
    baselineShare > 0

  const formatYAxis = (value: number): string => {
    if (mode === 'share') {
      return `${(value * 100).toFixed(1)}%`
    }
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`
    return String(Math.round(value))
  }

  const formatTooltip = (value: number): string => {
    if (mode === 'share') {
      return `${(value * 100).toFixed(2)}%`
    }
    return String(Math.round(value))
  }

  if (formattedData.length === 0) return null

  return (
    <ChartWrapper $height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={formattedData}
          margin={{ top: 10, right: 65, bottom: 20, left: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: '#888' }}
            tickLine={false}
            axisLine={{ stroke: '#ddd' }}
          />
          <YAxis
            allowDecimals={mode === 'share'}
            tickFormatter={formatYAxis}
            tick={{ fontSize: 12, fill: '#888' }}
            tickLine={false}
            axisLine={false}
            width={60}
          />
          <Tooltip
            formatter={(value: number) => [
              formatTooltip(value),
              mode === 'share' ? 'Share' : 'Mentions',
            ]}
            labelStyle={{ color: darkBlueFontColor, fontWeight: 600 }}
            contentStyle={{
              borderRadius: 8,
              border: '1px solid #eee',
              fontSize: 13,
            }}
          />
          <defs>
            <linearGradient id="issueAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_COLOR} stopOpacity={0.15} />
              <stop offset="100%" stopColor={CHART_COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke="none"
            fill="url(#issueAreaGrad)"
            legendType="none"
            tooltipType="none"
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={CHART_COLOR}
            strokeWidth={2}
            dot={{ r: 3, fill: CHART_COLOR, stroke: '#fff', strokeWidth: 1 }}
            activeDot={{
              r: 5,
              fill: CHART_COLOR,
              stroke: '#fff',
              strokeWidth: 2,
            }}
          />
          {showBaseline && (
            <ReferenceLine
              y={baselineShare}
              stroke={BASELINE_COLOR}
              strokeWidth={1.5}
              strokeDasharray="6 4"
              label={{
                value: 'Baseline',
                position: 'right',
                fill: BASELINE_COLOR,
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartWrapper>
  )
}

const ChartWrapper = styled.div<{ $height: string | number }>`
  width: 100%;
  height: ${(p) =>
    typeof p.$height === 'number' ? `${p.$height}px` : p.$height};
  min-height: 200px;
  position: relative;
`
